import {
  Breadcrumbs,
  Button,
  Inline,
  notifyError,
  Stack,
  Typography,
} from '@omnipresentgroup/design-system';
import { useGetContractorInvoice } from '@omnipresentgroup/domains';
import { ContractorInvoiceDetails } from '@omnipresentgroup/features';
import { ContractorInvoiceStateEnum } from '@omnipresentgroup/types';
import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';

import { Loading } from '../../../components/Loading';
import {
  CONTRACTOR_INVOICES_PATH,
  CONTRACTOR_INVOICES_PAYMENT_PATH,
} from '../../../constants';
import { useManagerUser } from '../../../hooks';
import { WrappedMixpanel } from '../../../mixpanelWrapper';
import { CONTRACTOR_INVOICES_PAGE_TITLE } from '../ContractorInvoices';
import { InvoiceNotification } from './components';

export const ContractorInvoiceView = () => {
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const navigate = useNavigate();

  const { userId } = useManagerUser();

  const {
    data: invoice,
    isLoading,
    isError,
    isSuccess,
  } = useGetContractorInvoice({
    id: invoiceId,
    enabled: Boolean(invoiceId),
  });

  useEffect(() => {
    (isError || (isSuccess && !invoice)) &&
      notifyError({
        title: 'Error',
        description:
          'Something went wrong while getting invoice information. Please contact support.',
      });
  }, [invoice, isError, isSuccess]);

  return (
    <>
      <Stack gap="16">
        <Breadcrumbs
          crumbs={[
            { id: 'home', title: 'Home', onClick: () => navigate('/') },
            {
              id: 'contractor-invoices',
              title: CONTRACTOR_INVOICES_PAGE_TITLE,
              onClick: () => navigate(CONTRACTOR_INVOICES_PATH),
            },
            {
              id: 'contractor-invoice-details',
              title: invoice?.invoiceRef ?? 'Invoice',
              active: true,
            },
          ]}
        />
        <Stack gap="0">
          <Inline justify="space-between" minH={40} align="center">
            <Inline gap="8" align="center">
              <Typography
                as="h2"
                hideParagraphSpacing
                size="24"
                weight="medium"
              >
                {invoice?.invoiceRef}
              </Typography>
            </Inline>
            {invoice?.state === ContractorInvoiceStateEnum.OPEN && (
              <Button
                onClick={() => {
                  WrappedMixpanel.track(
                    'Invoice Pay Clicked from Invoice Detail Page',
                    {
                      userId,
                    },
                  );
                  navigate(`${CONTRACTOR_INVOICES_PAYMENT_PATH}`, {
                    state: { invoicesIds: [invoice.id] },
                  });
                }}
              >
                Pay Invoice
              </Button>
            )}
          </Inline>
          <Typography as="span" size="14" color="subtle">
            {invoice?.contractor.name}
          </Typography>
        </Stack>
      </Stack>
      {isLoading ? (
        <Loading />
      ) : (
        <>
          {invoice && <InvoiceNotification invoice={invoice} />}
          {invoiceId && (
            <Inline justify="center">
              <Stack gap="24" maxW={600} w="stretch">
                <ContractorInvoiceDetails
                  invoiceId={invoiceId}
                  invoiceStatusOverride={{
                    [ContractorInvoiceStateEnum.OPEN]: {
                      intent: 'warning',
                      text: 'Open',
                    },
                    [ContractorInvoiceStateEnum.IN_PROGRESS]: {
                      text: 'Awaiting transfer',
                    },
                  }}
                  showPaymentDetails
                />
              </Stack>
            </Inline>
          )}
        </>
      )}
    </>
  );
};
